import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, BookOpen, BarChart3,
  ClipboardCheck, MessageSquare, ListChecks, Bell,
  LogOut, X, FileText, Settings, Shield, Clock, CalendarDays, MoreHorizontal, Brain, Library, Video, Receipt, ChevronLeft, Building2
} from 'lucide-react';

import { PushNotificationToggle } from '@/components/PushNotificationToggle';
import { SessionBlockingOverlay } from '@/components/SessionBlockingOverlay';
import { InstallBanner } from '@/components/InstallBanner';

export default function Layout() {
  const { user, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const unread = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await api.get('/notifications');
        setNotifications(res.data);
        // Update app badge count on home screen
        const unreadCount = res.data.filter(n => !n.read).length;
        if ('setAppBadge' in navigator) {
          unreadCount > 0 ? navigator.setAppBadge(unreadCount) : navigator.clearAppBadge();
        }
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
    };
    const fetchUnreadMessages = async () => {
      try {
        const res = await api.get('/messages/unread-count');
        setUnreadMessages(res.data.unread_count || 0);
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
    };
    fetchNotifs();
    fetchUnreadMessages();
    const interval = setInterval(() => { fetchNotifs(); fetchUnreadMessages(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close more menu on route change
  useEffect(() => { setShowMore(false); }, [location.pathname]);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    if ('clearAppBadge' in navigator) navigator.clearAppBadge();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isInstructor = user?.role === 'instructor';
  const isAdmin = user?.is_admin;
  const isFacilityManager = user?.role === 'facility_manager';
  const userRoles = user?.roles || [user?.role];
  const hasMultipleRoles = userRoles.length > 1;

  const handleSwitchRole = async (newRole) => {
    await switchRole(newRole);
    navigate('/dashboard');
  };

  const roleLabels = { instructor: 'Instructor', parent: 'Parent', facility_manager: 'Facility Manager' };

  // Lock navigation during assessment flow (instructor must complete before leaving)
  const isAssessmentLocked = location.pathname.includes('/assessment/');

  // All nav items
  const allNavItems = isFacilityManager ? [
    { to: '/dashboard', icon: Building2, label: 'Facility', end: true },
    { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
  ] : isInstructor ? [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/dashboard/schedule', icon: CalendarDays, label: 'Schedule' },
    { to: '/dashboard/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/dashboard/practice-videos/select', icon: Video, label: 'Practice' },
    { to: '/dashboard/ai-insights/select', icon: Brain, label: 'Insights' },
    { to: '/dashboard/knowledge', icon: Library, label: 'Knowledge' },
    { to: '/dashboard/storyboard/select', icon: BookOpen, label: 'Storyboard' },
    { to: '/dashboard/criteria/select', icon: ListChecks, label: 'Criteria' },
    { to: '/dashboard/progress/select', icon: BarChart3, label: 'Progress' },
    { to: '/dashboard/reports/select', icon: FileText, label: 'Reports' },
    { to: '/dashboard/hours', icon: Clock, label: 'Hours' },
    { to: '/dashboard/expenses', icon: Receipt, label: 'Expenses' },
    ...(isAdmin ? [{ to: '/dashboard/admin', icon: Shield, label: 'Admin' }] : []),
    ...(isAdmin ? [{ to: '/dashboard/facility', icon: Building2, label: 'Facility' }] : []),
    { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/dashboard/schedule', icon: CalendarDays, label: 'Schedule' },
    { to: '/dashboard/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/dashboard/practice-videos/select', icon: Video, label: 'Practice' },
    { to: '/dashboard/storyboard/select', icon: BookOpen, label: 'Storyboard' },
    { to: '/dashboard/progress/select', icon: BarChart3, label: 'Progress' },
    { to: '/dashboard/reports/select', icon: FileText, label: 'Reports' },
    { to: '/dashboard/feedback/select', icon: ClipboardCheck, label: 'Feedback' },
    { to: '/dashboard/criteria/select', icon: ListChecks, label: 'Criteria' },
    { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
  ];

  // Bottom tab: first 4 items + More
  const bottomTabs = allNavItems.slice(0, 4);
  const moreItems = allNavItems.slice(4);

  // Check if a "More" item is currently active
  const isMoreActive = moreItems.some(item => {
    if (item.end) return location.pathname === item.to;
    return location.pathname.startsWith(item.to);
  });

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 pb-3">
        <NavLink to="/dashboard" className="flex items-center gap-3">
          <img src="/logo.png" alt="Big Effort Swim" className="w-12 h-12 rounded-xl object-contain" />
          <div>
            <span className="text-lg font-extrabold text-[#C4F017] block leading-tight" style={{ fontFamily: 'Nunito' }}>SplashAble</span>
            <span className="text-[10px] font-bold text-[#22D3EE] tracking-widest">BY BIG EFFORT SWIM</span>
          </div>
        </NavLink>
      </div>

      <div className="px-4 py-3">
        <div className="bg-[#2D2B66] rounded-2xl p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#22D3EE]/20 flex items-center justify-center text-[#22D3EE] font-bold text-lg" style={{ fontFamily: 'Nunito' }}>
            {user?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-white truncate text-sm">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
        {allNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `sidebar-link flex items-center gap-3 px-4 py-3 text-sm font-medium ${
                isActive ? 'active' : 'text-slate-300 hover:text-white'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="relative">
              <item.icon className="w-5 h-5" />
              {item.label === 'Messages' && unreadMessages > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-[#C4F017] rounded-full border-2 border-[#1E1B4B]" />
              )}
            </div>
            {item.label}
            {item.label === 'Messages' && unreadMessages > 0 && (
              <span className="ml-auto bg-[#C4F017] text-[#1E1B4B] text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadMessages}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-[#2D2B66]">
        <Button
          data-testid="logout-btn"
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start gap-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl"
        >
          <LogOut className="w-5 h-5" /> Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0FDFA] flex pt-safe pb-safe" data-testid="app-layout">
      {/* Desktop sidebar — hidden during assessment */}
      {!isAssessmentLocked && (
        <aside className="hidden lg:block w-64 bg-[#1E1B4B] fixed h-screen z-30">
          <SidebarContent />
        </aside>
      )}

      {/* Main content */}
      <div className={`flex-1 min-w-0 ${isAssessmentLocked ? '' : 'lg:ml-64'}`}>
        {/* Top bar */}
        {/* Locked header during assessment */}
        {isAssessmentLocked ? (
          <header className="sticky top-0 z-20 bg-[#1E1B4B] px-4 lg:px-8 h-14 lg:h-16 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="SplashAble" className="w-8 h-8 rounded-lg object-contain" />
              <span className="text-base font-extrabold text-white" style={{ fontFamily: 'Nunito' }}>Post-Session Assessment</span>
            </div>
          </header>
        ) : (
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-slate-200/50 px-3 sm:px-4 lg:px-8 h-14 lg:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {/* Back button — shows when not on root dashboard */}
            {location.pathname !== '/dashboard' && (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full h-9 w-9 text-slate-500 hover:text-[#0E7490] hover:bg-[#ECFEFF] shrink-0"
                onClick={() => navigate(-1)}
                data-testid="back-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            )}
            {/* Mobile/Tablet: show logo */}
            <div className="lg:hidden flex items-center gap-1.5 min-w-0">
              <img src="/logo.png" alt="SplashAble" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-contain shrink-0" />
              <span className="text-sm font-extrabold text-[#1E1B4B] truncate" style={{ fontFamily: 'Nunito' }}>SplashAble</span>
            </div>
            <h2 className="text-lg font-bold text-[#1E1B4B] hidden lg:block" style={{ fontFamily: 'Nunito' }}>
              {isFacilityManager ? 'Facility Portal' : isInstructor ? 'Instructor Portal' : 'Parent Portal'}
            </h2>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Role Switcher */}
            {hasMultipleRoles && (
              <div className="flex items-center bg-slate-100 rounded-full p-0.5" data-testid="role-switcher">
                {userRoles.map(r => {
                  const shortLabels = { instructor: 'Instr', parent: 'Parent', facility_manager: 'Facility' };
                  return (
                    <button
                      key={r}
                      onClick={() => r !== user?.role && handleSwitchRole(r)}
                      className={`px-2 lg:px-3 py-1 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold transition-all whitespace-nowrap ${
                        r === user?.role ? 'bg-[#1E1B4B] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid={`switch-role-${r}`}
                    >
                      <span className="sm:hidden">{shortLabels[r] || r}</span>
                      <span className="hidden sm:inline">{roleLabels[r] || r}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="hidden lg:block"><PushNotificationToggle /></div>
            <div className="relative shrink-0">
              <Button
                data-testid="notification-bell"
                variant="ghost"
                size="icon"
                className="rounded-full relative h-10 w-10"
                onClick={() => setShowNotifs(!showNotifs)}
              >
                <Bell className="w-5 h-5 text-[#1E1B4B]" />
                {unread > 0 && (
                  <span className="absolute top-0 right-0 min-w-[18px] h-[18px] bg-[#C4F017] text-[#1E1B4B] text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unread}
                  </span>
                )}
              </Button>

              {showNotifs && (
                <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-100 z-50 max-h-96 overflow-auto" data-testid="notifications-dropdown">
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>Notifications</h3>
                    {unread > 0 && (
                      <button onClick={markAllRead} className="text-xs text-[#22D3EE] font-semibold hover:underline">Mark all read</button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="p-4 text-center text-slate-400 text-sm">No notifications yet</p>
                  ) : (
                    notifications.slice(0, 10).map(n => (
                      <div
                        key={n.notification_id}
                        className={`p-4 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? 'bg-cyan-50/50' : ''}`}
                        onClick={() => {
                          markRead(n.notification_id);
                          setShowNotifs(false);
                          if (n.link) navigate(n.link);
                        }}
                      >
                        <p className="text-sm text-slate-700">{n.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
        )}

        {/* Page content — add bottom padding on mobile/tablet for tab bar */}
        {!isAssessmentLocked && <InstallBanner />}
        <main className={`p-4 sm:p-6 lg:p-8 overflow-x-hidden ${isAssessmentLocked ? 'pb-8' : 'pb-32 lg:pb-8'}`}>
          <Outlet />
        </main>
      </div>

      {/* Bottom Tab Bar — phone & tablet only */}
      {/* Mobile Tab Bar — hidden during assessment */}
      {!isAssessmentLocked && (
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]" data-testid="bottom-tab-bar" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-around h-[72px] max-w-lg mx-auto px-2">
          {bottomTabs.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl min-w-[60px] transition-colors ${
                  isActive ? 'text-[#0E7490]' : 'text-slate-400'
                }`
              }
              data-testid={`tab-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {({ isActive }) => (
                <>
                  <div className="relative">
                    <item.icon className={`w-6 h-6 ${isActive ? 'text-[#0E7490]' : 'text-slate-400'}`} />
                    {item.label === 'Messages' && unreadMessages > 0 && (
                      <span className="absolute -top-1 -right-2 w-4 h-4 bg-[#C4F017] text-[#1E1B4B] text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadMessages}
                      </span>
                    )}
                  </div>
                  <span className={`text-[11px] font-semibold leading-tight ${isActive ? 'text-[#0E7490]' : 'text-slate-400'}`}>
                    {item.label}
                  </span>
                  {isActive && <div className="w-5 h-0.5 bg-[#22D3EE] rounded-full" />}
                </>
              )}
            </NavLink>
          ))}

          {/* More button */}
          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl min-w-[60px] transition-colors ${
              isMoreActive || showMore ? 'text-[#0E7490]' : 'text-slate-400'
            }`}
            data-testid="tab-more"
          >
            <MoreHorizontal className={`w-6 h-6 ${isMoreActive || showMore ? 'text-[#0E7490]' : 'text-slate-400'}`} />
            <span className={`text-[11px] font-semibold leading-tight ${isMoreActive || showMore ? 'text-[#0E7490]' : 'text-slate-400'}`}>
              More
            </span>
            {isMoreActive && !showMore && <div className="w-5 h-0.5 bg-[#22D3EE] rounded-full" />}
          </button>
        </div>
      </nav>
      )}

      {/* More menu overlay — phone & tablet */}
      {showMore && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="more-menu-overlay">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowMore(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-xl animate-slideUp max-h-[70vh] overflow-auto">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h3 className="font-bold text-[#1E1B4B] text-lg" style={{ fontFamily: 'Nunito' }}>Menu</h3>
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setShowMore(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* User info */}
            <div className="px-5 pb-3">
              <div className="bg-[#F0FDFA] rounded-2xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#22D3EE]/20 flex items-center justify-center text-[#0E7490] font-bold text-lg" style={{ fontFamily: 'Nunito' }}>
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate text-sm">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </div>
            </div>

            <div className="px-3 pb-5 space-y-1">
              {moreItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setShowMore(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-4 rounded-xl text-base font-medium transition-colors ${
                      isActive ? 'bg-[#ECFEFF] text-[#0E7490]' : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                  data-testid={`more-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}

              {/* Push Notifications toggle — mobile */}
              <div className="px-4 py-3 sm:hidden space-y-2">
                <PushNotificationToggle />
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post('/push/test');
                      const d = res.data;
                      if (d.notifications_sent > 0) {
                        toast.success('Test notification sent! Check your phone.');
                      } else if (d.subscriptions_found === 0) {
                        toast.error('No push subscription found. Try disabling and re-enabling notifications.');
                      } else {
                        toast.error('Push failed to send. Check notification permissions.');
                      }
                    } catch { toast.error('Failed to send test notification'); }
                  }}
                  className="text-xs text-slate-400 hover:text-[#0E7490] underline"
                  data-testid="test-push-btn"
                >
                  Send test notification
                </button>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-4 rounded-xl text-base font-medium text-red-500 hover:bg-red-50 w-full transition-colors mt-2"
                data-testid="more-logout"
              >
                <LogOut className="w-5 h-5" /> Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Session Blocking Overlay — for instructors with overdue sessions */}
      {isInstructor && <SessionBlockingOverlay />}
    </div>
  );
}
